import {
  FdpInvoiceCommentResponse,
  InvoiceComment,
} from "../../../../app/models/responseModels/ClientInvoiceResponses";
import {
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
  IconButton,
  TextField,
  Skeleton,
  Button,
} from "@mui/material";
import { Edit, Delete, Save, Cancel } from "@mui/icons-material";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import getAxiosError from "../../../../app/api/axiosError";
import { useAppDispatch } from "../../../../app/features/store/store";
import CommentInputModal from "../../../clients/invoices/modals/CommenInputModal";
import { RatecardFdpInvoiceResponse } from "../../../../app/models/responseModels/FdpInvoiceResponses";
import { FdpInvoiceAgent } from "../../../../app/api/fdpInvoices/FdpInvoiceAgent";
import { FdpInvoiceCommentRequest } from "../../../../app/models/requestModels/ClientInvoiceRequests";

interface FdpInvoiceCommentProps {
  invoice: RatecardFdpInvoiceResponse | undefined;
  actionStatus: string;
  handleAction: CallableFunction;
  newCommentModal: boolean;
  closeNewCommentModal: CallableFunction;
}

const FdpInvoiceComment = (props: FdpInvoiceCommentProps) => {
  const { newCommentModal, closeNewCommentModal } = props;
  const dispatch = useAppDispatch();

  // State to track which comment is being edited
  const [isCommentsLoading, setCommentsLoading] = useState<boolean>(false);
  const [showCommentBox, setShowCommentBox] = useState<boolean>(false);
  const [openNewCommentModal, setOpenNewCommentModal] =
    useState<boolean>(false);
  const [invoiceComments, setInvoiceComments] =
    useState<FdpInvoiceCommentResponse>();
  const [editedCommentId, setEditedCommentId] = useState<number | null>(null);
  const [editedCommentText, setEditedCommentText] = useState<string>("");
  const [editReplyId, setEditReplyId] = useState<number | null>(null);
  const [editCommentReplyText, setEditCommentReplyText] = useState<string>("");

  const getInvoiceComments = useCallback(async () => {
    setCommentsLoading(true);
    try {
      const response = await FdpInvoiceAgent.getFdpInvoiceComments(
        props.invoice?.id as number
      );
      if (response.isSuccess) {
        setInvoiceComments(response.data);
      }
      if (!response.isSuccess) {
        toast.error(response.message!, {
          toastId: "invoiceCommentListException",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "invoiceCommentListException",
      });
    } finally {
      setCommentsLoading(false);
    }
  }, [dispatch, props.invoice?.id]);

  useEffect(() => {
    if (props.invoice?.id) {
      getInvoiceComments();
    }
  }, [props.invoice?.id, getInvoiceComments]);

  useEffect(() => {
    setOpenNewCommentModal(newCommentModal);
  }, [newCommentModal]);

  useEffect(() => {
    if (
      invoiceComments?.commentDetails &&
      invoiceComments?.commentDetails.length > 0
    ) {
      setShowCommentBox(true);
    } else {
      setShowCommentBox(false);
    }
  }, [invoiceComments?.commentDetails]);

  const handleEditClick = (comment: InvoiceComment) => {
    setEditedCommentId(comment.id);
    setEditedCommentText(comment.comment); // Initialize with current comment text
  };

  const handleEditReplyClick = (comment: InvoiceComment) => {
    setEditReplyId(comment.id);
    setEditCommentReplyText(comment.replyComment); // Initialize with current comment reply text
  };

  const handleResetEdits = () => {
    setEditedCommentId(null);
    setEditReplyId(null);
    setEditedCommentText("");
    setEditCommentReplyText("");
  };

  const handleNewComment = async (commentText: string) => {
    try {
      setCommentsLoading(true);
      if (props.invoice) {
        const commentRequest: FdpInvoiceCommentRequest = {
          invoicePkId: props.invoice.id,
          comment: commentText,
          status: props.actionStatus, // <-- Send the action status to backend

        };
        const response = await FdpInvoiceAgent.addFdpInvoiceComment(
          commentRequest
        );
        if (response.isSuccess) {
          setInvoiceComments(response.data);
          if (props.actionStatus) {
            props.handleAction(props.actionStatus);
          }
          toast.success("Fdp invoice flagged for OPS Review", {
            toastId: "FdpInvoiceNewComment",
          });
        } else
          toast.error("Failed to flagged for OPS Review", {
            toastId: "FdpInvoiceNewCommentException",
          });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(`Failed to comment- ${appError.errorMessage}`, {
        toastId: "FdpInvoiceNewCommentException",
      });
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleEditComment = async (comment: InvoiceComment) => {
    try {
      setCommentsLoading(true);
      if (props.invoice) {
        const commentRequest: FdpInvoiceCommentRequest = {
          invoicePkId: props.invoice.id,
          commentId: comment.id,
          comment: editedCommentText ? editedCommentText : comment.comment,
          replyComment: editCommentReplyText
            ? editCommentReplyText
            : comment.replyComment,
          status: props.actionStatus
        };
        const response = await FdpInvoiceAgent.editFdpInvoiceComment(
          commentRequest
        );
        if (response.isSuccess) {
          setInvoiceComments(response.data);
          if (editCommentReplyText) {
            toast.success("Comment Resolved!", {
              toastId: "CommentEdit",
            });
          } else {
            toast.success("Comment edited successfully!", {
              toastId: "CommentEdit",
            });
          }
        } else
          toast.error("Failed to edit comment", {
            toastId: "CommentEditException",
          });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(`Failed to edit comment- ${appError.errorMessage}`, {
        toastId: "FdpInvoiceApproveException",
      });
    } finally {
      setCommentsLoading(false);
      handleResetEdits(); // Exit edit mode
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    try {
      setCommentsLoading(true);
      if (props.invoice) {
        const response = await FdpInvoiceAgent.deleteFdpInvoiceComment(
          commentId
        );
        if (response.isSuccess) {
          setInvoiceComments(response.data);
          toast.success("Comment deleted.", {
            toastId: "FdpInvoiceDelete",
          });
        } else
          toast.error("Failed to delete comment", {
            toastId: "FdpInvoiceDeleteException",
          });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(`Failed to delete comment- ${appError.errorMessage}`, {
        toastId: "FdpInvoiceDeleteException",
      });
    } finally {
      setCommentsLoading(false);
    }
  };

  return (
    <>
      {(isCommentsLoading || showCommentBox) && (
        <Box mt={1} sx={{ maxHeight: "40vh" }}>
          <List
            sx={{
              width: "100%",
              maxWidth: 750,
              bgcolor: "#f1efef",
              maxHeight: "40vh",
              overflowY: "auto",
            }}
          >
            <Typography variant="h5" ml={2} sx={{ color: "#0c640c" }}>
              Comment
            </Typography>
            {isCommentsLoading && (
              <Box padding={2}>
                <Box mb={2}>
                  <Skeleton
                    variant="rectangular"
                    height={40}
                    animation="pulse"
                  />
                </Box>
                <Box mb={2}>
                  <Skeleton
                    variant="rectangular"
                    height={40}
                    animation="wave"
                  />
                </Box>
                <Box mb={2}>
                  <Skeleton
                    variant="rectangular"
                    height={40}
                    animation="pulse"
                  />
                </Box>
              </Box>
            )}
            {!isCommentsLoading &&
              invoiceComments?.commentDetails.map(
                (comment: InvoiceComment, index: number) => (
                  <React.Fragment key={comment.id}>
                    <ListItem sx={{ marginBottom: -3 }} alignItems="flex-start">
                      <ListItemText
                        primary={
                          <Box component="span">
                            <Typography variant="h6" component="span">
                              {new Date(comment.createdOn).toLocaleString(
                                "en-US",
                                {
                                  hourCycle: "h12",
                                }
                              )}
                            </Typography>
                            {/* <Typography variant="h6" component="span" ml={1}>
                              Author: {comment.author}
                            </Typography> */}
                            <Typography
                              variant="h6"
                              ml={2}
                              component="span"
                              sx={{ color: "#0c640c" }}
                            >
                              {comment.status}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          editedCommentId === comment.id &&
                          comment.commentEditable ? (
                            <TextField
                              fullWidth
                              multiline
                              value={editedCommentText}
                              onChange={(e) =>
                                setEditedCommentText(e.target.value)
                              }
                            />
                          ) : (
                            <Typography
                              component="span"
                              variant="body1"
                              sx={{
                                color: "text.primary",
                                display: "inline",
                              }}
                            >
                              {comment.comment}
                            </Typography>
                          )
                        }
                      />
                      {editedCommentId === comment.id &&
                      comment.commentEditable ? (
                        // Show Save and Cancel buttons if in edit mode
                        <>
                          <IconButton
                            edge="end"
                            disabled={!editedCommentText}
                            color="primary"
                            aria-label="save"
                            onClick={() => handleEditComment(comment)}
                          >
                            <Save />
                          </IconButton>
                          <IconButton
                            edge="end"
                            color="primary"
                            aria-label="cancel"
                            onClick={handleResetEdits}
                          >
                            <Cancel />
                          </IconButton>
                        </>
                      ) : (
                        // Show Edit and Delete buttons otherwise
                        <>
                          {comment.commentEditable && (
                            <IconButton
                              edge="end"
                              color="primary"
                              aria-label="edit"
                              onClick={() => handleEditClick(comment)}
                            >
                              <Edit />
                            </IconButton>
                          )}
                          {comment.commentDeletable && (
                            <IconButton
                              edge="end"
                              color="primary"
                              aria-label="delete"
                              onClick={() => handleDeleteComment(comment.id)}
                            >
                              <Delete />
                            </IconButton>
                          )}
                        </>
                      )}
                    </ListItem>
                    <ListItem sx={{ marginBottom: -1, ml: 2, maxWidth: 600 }}>
                      {/* Indent for replies */}
                      <ListItemText
                        primary={
                          <Box mt={0.5}>
                            {comment.replyComment ? (
                              <>
                                <Typography
                                  component="span"
                                  variant="body2"
                                  sx={{
                                    color: "text.primary",
                                    display: "inline",
                                  }}
                                >
                                  <b>{comment.replier}:</b>{" "}
                                  {comment.replyComment}
                                </Typography>
                                <br />
                                {editReplyId === comment.id ? (
                                  <>
                                    <TextField
                                      fullWidth
                                      multiline
                                      value={editCommentReplyText}
                                      onChange={(e) =>
                                        setEditCommentReplyText(e.target.value)
                                      }
                                    />
                                    <IconButton
                                      edge="end"
                                      color="primary"
                                      aria-label="save"
                                      disabled={!editCommentReplyText}
                                      onClick={() => handleEditComment(comment)}
                                    >
                                      <Save />
                                    </IconButton>
                                    <IconButton
                                      edge="end"
                                      color="primary"
                                      aria-label="cancel"
                                      onClick={handleResetEdits}
                                    >
                                      <Cancel />
                                    </IconButton>
                                  </>
                                ) : (
                                  comment.commentReplyEditable && (
                                    <IconButton
                                      edge="end"
                                      color="primary"
                                      aria-label="edit"
                                      onClick={() =>
                                        handleEditReplyClick(comment)
                                      }
                                    >
                                      <Edit />
                                    </IconButton>
                                  )
                                )}
                              </>
                            ) : (
                              <>
                                {editReplyId === comment.id &&
                                comment.commentRepliable ? (
                                  <>
                                    <TextField
                                      fullWidth
                                      multiline
                                      value={editCommentReplyText}
                                      onChange={(e) =>
                                        setEditCommentReplyText(e.target.value)
                                      }
                                    />
                                    <IconButton
                                      edge="end"
                                      color="primary"
                                      disabled={!editCommentReplyText}
                                      aria-label="save"
                                      onClick={() => handleEditComment(comment)}
                                    >
                                      <Save />
                                    </IconButton>
                                    <IconButton
                                      edge="end"
                                      color="primary"
                                      aria-label="cancel"
                                      onClick={handleResetEdits}
                                    >
                                      <Cancel />
                                    </IconButton>
                                  </>
                                ) : (
                                  comment.commentRepliable && (
                                    <Button
                                      onClick={() =>
                                        handleEditReplyClick(comment)
                                      }
                                    >
                                      Resolve It
                                    </Button>
                                  )
                                )}
                              </>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < invoiceComments.commentDetails.length - 1 && (
                      <Divider
                        variant="middle"
                        sx={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
                        component="li"
                      />
                    )}
                  </React.Fragment>
                )
              )}
          </List>
        </Box>
      )}

      <CommentInputModal
        open={openNewCommentModal}
        onClose={() => {
          setOpenNewCommentModal(!openNewCommentModal);
          closeNewCommentModal();
        }}
        asyncSubmitOperation={handleNewComment}
        title={"Flag Invoice For Review"}
        submitButtonName={"Submit"}
      />
    </>
  );
};

export default FdpInvoiceComment;
